import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import App, { type SidebarLayoutState } from './App.tsx';
import { Auth } from './components/auth/Auth';
import { ChatBotWidget } from './components/ChatBot';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ApiErrorBoundary } from './components/ApiErrorBoundary';
import type { AuthProps } from './types/auth';
import { sidebarBridge } from './services/sidebar-bridge';
import { reactComponentRegistry } from './registry';
// Import complete Equal DS bundle (pre-scoped, all utilities included)
import 'equal-ds-ui/styles.css';
import './index.css';
import './App.css';
import './components/Header.css';

// For development mode - render directly
if (import.meta.env.DEV) {
  const root = ReactDOM.createRoot(document.getElementById('root')!);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <Provider store={store}>
          <ApiErrorBoundary>
            <Auth />  {/* Changed from App to Auth to view login form */}
          </ApiErrorBoundary>
        </Provider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

// Unified mounting interface for Angular integration
const mountedInstances = new Map<string, ReactDOM.Root>();

interface ReactAuthOptions extends Partial<AuthProps> {
  containerId?: string;
  className?: string;
  showRememberMe?: boolean;
}

interface MountSidebarOptions {
  menuList?: any[];
  userInfo?: any;
  currentRoute?: string;
  onNavigate?: (route: string) => void;
  headerContainerId?: string;
  onLayoutChange?: (state: SidebarLayoutState) => void;
}

// Generic page mount options
interface PageMountOptions {
  componentId: string;
  routeParams?: Record<string, string>;
  queryParams?: Record<string, string>;
  currentRoute?: string;
}

// ChatBot mount options
interface ChatBotMountOptions {
  userId?: string;
  userName?: string;
  userRole?: string;
  environment?: 'sandbox' | 'production';
}

// Declare global types for Angular integration
interface ReactAppAPI {
  // Generic page mounting (NEW - Component Registry Pattern)
  mount: (containerId: string, options: PageMountOptions) => void;

  // Auth methods
  mountAuth: (containerId: string, options?: ReactAuthOptions) => void;

  // Sidebar/Navigation methods
  mountSidebar: (containerId: string, props?: MountSidebarOptions) => void;
  mountHeader?: (containerId: string, props?: any) => void;

  // ChatBot methods
  mountChatBot: (containerId: string, options?: ChatBotMountOptions) => void;

  // Shared methods
  unmount: (containerId: string) => void;
  unmountAll: () => void;

  // Store access
  getStore: () => typeof store;
  getAuthState: () => any;
  subscribe: (callback: (state: any) => void) => () => void;

  // Navigation helpers
  goToStep: (step: string) => void;
}

interface LegacyReactAuthAPI {
  // Note: Does NOT extend ReactAppAPI to avoid mount() signature conflict
  // Auth-specific methods
  mountAuth: (containerId: string, options?: ReactAuthOptions) => void;
  mountSidebar: (containerId: string, props?: MountSidebarOptions) => void;
  mountHeader?: (containerId: string, props?: any) => void;
  unmount: (containerId: string) => void;
  unmountAll: () => void;
  getStore: () => typeof store;
  getAuthState: () => any;
  subscribe: (callback: (state: any) => void) => () => void;
  goToStep: (step: string) => void;

  // Legacy auth-specific mount method for backward compatibility
  mount: (options?: ReactAuthOptions) => LegacyReactAuthAPI;
}

declare global {
  interface Window {
    ReactApp: ReactAppAPI;
    ReactAuth: LegacyReactAuthAPI;
    ReactSidebarApp: {
      mountSidebar: (containerId: string, props: MountSidebarOptions) => void;
      mount?: (containerId: string, props: MountSidebarOptions) => void;
      unmount: (containerId: string) => void;
    };
  }
}

// Main ReactApp API
window.ReactApp = {
  // ========================================
  // Generic Page Mounting (Component Registry Pattern)
  // ========================================

  /**
   * Mount any React page component by componentId
   * This is the new pattern for all page-level components
   *
   * @param containerId - DOM container ID
   * @param options - Mounting options including componentId and route data
   */
  mount: (containerId: string, options: PageMountOptions) => {
    const container = document.getElementById(containerId);
    if (!container) {
      return;
    }

    // If container already has a mounted instance, unmount it first
    // This handles the case where Angular creates a new wrapper before destroying the old one
    if (mountedInstances.has(containerId)) {
      const existingRoot = mountedInstances.get(containerId);
      if (existingRoot) {
        try {
          existingRoot.unmount();
          mountedInstances.delete(containerId);
        } catch (error) {
        }
      }
    }

    // Get component from registry service
    const PageComponent = reactComponentRegistry.getComponent(options.componentId);
    if (!PageComponent) {
      return;
    }

    // Prepare props from route data
    const componentProps = {
      routeParams: options.routeParams || {},
      queryParams: options.queryParams || {},
      currentRoute: options.currentRoute,
    };

    // Clear container before creating new root (React 18 best practice)
    container.innerHTML = '';

    // Create root and render with error boundaries
    const root = ReactDOM.createRoot(container);
    root.render(
      <ErrorBoundary>
        <Provider store={store}>
          <ApiErrorBoundary>
            <PageComponent {...componentProps} />
          </ApiErrorBoundary>
        </Provider>
      </ErrorBoundary>
    );

    mountedInstances.set(containerId, root);
  },

  // ========================================
  // Specific Component Mounting (Legacy/Specialized)
  // ========================================

  // Mount Auth component
  mountAuth: (containerId: string, options: ReactAuthOptions = {}) => {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container with id "${containerId}" not found`);
      return;
    }

    // Prevent double mounting
    if (mountedInstances.has(containerId)) {
      console.warn(`Component already mounted in "${containerId}"`);
      return;
    }

    const root = ReactDOM.createRoot(container);
    root.render(
      <ErrorBoundary>
        <Provider store={store}>
          <ApiErrorBoundary onRetry={() => window.location.reload()}>
            <Auth
              onAuthSuccess={options.onAuthSuccess}
              onAuthError={options.onAuthError}
              className={options.className}
              showRememberMe={options.showRememberMe}
            />
          </ApiErrorBoundary>
        </Provider>
      </ErrorBoundary>
    );
    mountedInstances.set(containerId, root);
  },

  // Mount Sidebar component
  mountSidebar: (containerId: string, props: MountSidebarOptions = {}) => {
    console.log('[ReactSidebarApp] Mounting sidebar:', { containerId, props });

    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`[ReactSidebarApp] Container not found: ${containerId}`);
      return;
    }

    if (mountedInstances.has(containerId)) {
      console.warn(`[ReactSidebarApp] Sidebar already mounted in: ${containerId}`);
      return;
    }

    sidebarBridge.initialize({
      onNavigate: props.onNavigate,
      getActiveRoute: () => props.currentRoute || '',
    });

    const activeItemId = sidebarBridge.updateActiveRoute(props.currentRoute || '');
    const headerContainer = props.headerContainerId
      ? document.getElementById(props.headerContainerId)
      : null;

    const root = ReactDOM.createRoot(container);
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <Provider store={store}>
            <App
              activeItemId={activeItemId}
              headerContainer={headerContainer || undefined}
              onLayoutChange={(state) => props.onLayoutChange?.(state)}
            />
          </Provider>
        </ErrorBoundary>
      </React.StrictMode>
    );

    mountedInstances.set(containerId, root);
    console.log('[ReactSidebarApp] Sidebar mounted successfully');
  },

  // Header mount is managed via mountSidebar; kept for backward compatibility
  mountHeader: () => {
    console.warn('[ReactSidebarApp] mountHeader is deprecated; header is mounted via mountSidebar options.');
  },

  // Mount ChatBot component independently
  mountChatBot: (containerId: string, options: ChatBotMountOptions = {}) => {
    const container = document.getElementById(containerId);
    if (!container) {
      return;
    }

    if (mountedInstances.has(containerId)) {
      return;
    }

    // Get user data from sessionStorage if not provided
    const getUserData = () => {
      if (options.userId && options.userName && options.userRole) {
        return options;
      }

      try {
        const userinfoList = sessionStorage.getItem('loggedUserInfo');
        if (userinfoList) {
          const userinfo = JSON.parse(atob(userinfoList));
          return {
            userId: userinfo['user_id'] || userinfo['id'] || '',
            userName: userinfo['name'] || 'User',
            userRole: userinfo['role_name'] || userinfo['role'] || '',
            environment: (sessionStorage.getItem('environment') || 'production') as 'sandbox' | 'production'
          };
        }
      } catch (error) {
      }

      return {
        userId: sessionStorage.getItem('currentUserId') || '',
        userName: sessionStorage.getItem('currentUserName') || 'User',
        userRole: sessionStorage.getItem('userRole') || '',
        environment: (sessionStorage.getItem('environment') || 'production') as 'sandbox' | 'production'
      };
    };

    const userData = getUserData();

    const root = ReactDOM.createRoot(container);
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <Provider store={store}>
            <div className="equal-ds-root">
              <ChatBotWidget
                userId={userData.userId || undefined}
                userName={userData.userName !== 'User' ? userData.userName : undefined}
                userRole={userData.userRole || undefined}
                environment={userData.environment}
              />
            </div>
          </Provider>
        </ErrorBoundary>
      </React.StrictMode>
    );

    mountedInstances.set(containerId, root);
  },

  // Unmount specific component
  unmount: (containerId: string) => {
    console.log('🔧 ReactApp.unmount called with:', containerId);
    const root = mountedInstances.get(containerId);
    if (root) {
      root.unmount();
      mountedInstances.delete(containerId);
      console.log('✅ React App unmounted successfully!');
    }
  },

  // Unmount all components
  unmountAll: () => {
    mountedInstances.forEach((root) => {
      root.unmount();
    });
    mountedInstances.clear();
  },

  // Store access
  getStore: () => store,

  getAuthState: () => store.getState().auth,

  subscribe: (callback: (state: any) => void) => {
    return store.subscribe(() => {
      callback(store.getState());
    });
  },

  // Auth navigation helper
  goToStep: (step: string) => {
    const event = new CustomEvent('react-auth-step-change', {
      detail: { step }
    });
    window.dispatchEvent(event);
  },
};

// Backward compatibility - map old APIs to new unified API
window.ReactAuth = {
  mountAuth: window.ReactApp.mountAuth,
  mountSidebar: window.ReactApp.mountSidebar,
  mountHeader: window.ReactApp.mountHeader,
  unmount: window.ReactApp.unmount,
  unmountAll: window.ReactApp.unmountAll,
  getStore: window.ReactApp.getStore,
  getAuthState: window.ReactApp.getAuthState,
  subscribe: window.ReactApp.subscribe,
  goToStep: window.ReactApp.goToStep,

  // Legacy mount method for auth
  mount: (options?: ReactAuthOptions): LegacyReactAuthAPI => {
    const containerId = options?.containerId || 'react-auth-root';
    window.ReactApp.mountAuth(containerId, options);
    return window.ReactAuth; // Return for chaining
  },
} as LegacyReactAuthAPI;

window.ReactSidebarApp = {
  mountSidebar: (containerId: string, props: MountSidebarOptions = {}) => {
    window.ReactApp.mountSidebar(containerId, props);
  },
  // Backwards compatibility with legacy callers
  mount: (containerId: string, props: MountSidebarOptions = {}) => {
    console.log('🔧 ReactSidebarApp.mount called with:', { containerId, props });
    window.ReactApp.mountSidebar(containerId, props);
  },
  unmount: (containerId: string) => {
    console.log('🔧 ReactSidebarApp.unmount called with:', containerId);
    window.ReactApp.unmount(containerId);
  },
};

// Export for direct React usage
export { Auth } from './components/auth/Auth';
export { store } from './store';
